import { useTranslation } from "@i18n";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import React, { useRef, useMemo, forwardRef } from "react";
import { convertDate } from "@Services/luxonHelper";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import img403 from "@Assets/images/noAccess.svg";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import FormFlowLibraryPopup from "./formFlowLibraryPopup";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { formFlowServiceUrl } from "@Services/dataManager";
import _ from "lodash";
import { getUserSelection } from "../CreateInspectionAndProgress/formsHelpers";
import Text from "@CubeComponents/html/Text/Text";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getProjectSettingsPageUrl } from "@Features/projects";
import SelectSpaces from "@Components/WorkspaceList/SelectSpaces";
import useFormFlowList from "./useFormFlowList";
import { FORM_FLOWS_GRID_TYPE, getFormFlowsSelectedLayoutKey } from "@Helpers/constants";
import type { FormFlowRow } from "./formFlow.types";
import type {
  GridClickEvent,
  GridRowBase,
  LayoutData,
  SlickgridReactInstanceX,
  ToolbarItem,
  ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";

/**
 * The grid instance this list holds a ref to, and hands to `formFlowLibraryPopup`.
 *
 * Parameterised with `GridRowBase` rather than `FormFlowRow`: the row type carries an open index
 * signature (list columns are addressed by string field name), which makes `GridRowBase`'s own
 * optional members resolve to `unknown` and so fails the `T extends GridRowBase` constraint. Rows
 * read back off the ref are still named as `FormFlowRow` in `getRowsOf`.
 */
export type FormFlowGridRef = SlickgridReactInstanceX<GridRowBase>;

/**
 * `detail` of the CustomEvent SlickGrid1 dispatches on a selection change. SlickGrid1 types the
 * callback's argument as an open `Record<string, unknown>`, so `detail` arrives as `unknown` and is
 * named here at the one place it is read.
 */
interface GridSelectionDetail {
  args: { rows: number[] };
}

interface FormFlowsProps {
  projectId?: string;
  /** Opens a flow for editing. */
  setFormFlowId?: (formFlowId: string) => void;
  /** Selection-only mode: no toolbar, no header. */
  showOnlyGrid?: boolean;
  /** Replaces the default project query (used by the library picker). */
  customQuery?: unknown;
  /** Lets an embedding wizard enable its own Next button from this list's selection. */
  setNextBtnState?: (state: boolean) => void;
  /** Replaces the default list endpoint (used by the library picker). */
  customService?: string;
}

const FormFlows = forwardRef<FormFlowGridRef, FormFlowsProps>(
  (
    { projectId, setFormFlowId, showOnlyGrid = false, customQuery, setNextBtnState, customService },
    ref: React.ForwardedRef<FormFlowGridRef>,
  ) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [urlSearchParams] = useSearchParams();
    const formCategory = urlSearchParams.get("resourceType")?.toUpperCase();

    const tempRef = useRef<FormFlowGridRef | null>(null);
    // Callers only ever pass an object ref (`formFlowLibraryPopup` passes a `useRef`) — the same
    // assertion SlickGrid1 itself makes when it accepts a forwarded ref. Erased at emit.
    const dataGrid = (ref as React.MutableRefObject<FormFlowGridRef | null>) || tempRef;
    const archivedDataGrid = useRef<FormFlowGridRef | null>(null);

    /** Reads a grid's current selection as row data. */
    const getRowsOf = (gridRef: React.MutableRefObject<FormFlowGridRef | null>): FormFlowRow[] =>
      gridRef.current?.slickGrid
        .getSelectedRows()
        .map((row: number) => gridRef.current!.slickGrid.getDataItem(row) as FormFlowRow) ?? [];

    const {
      hasAddPermission,
      listQuery,
      listService,
      archivedListQuery,
      columns,
      toolbarOptions,
      archivedToolbarOptions,
      createMenuItems,
      isLoadingComplete,
      archivedIsLoadingComplete,
      handleListLoaded,
      handleArchivedListLoaded,
      handleSelectionChange,
      handleArchivedSelectionChange,
      handleRecordClick,
      openAddEditModal,
      showDeleteConfirmationModal,
      showArchiveModal,
      addFromLibrary,
      setAddFromLibrary,
      openCreateModal,
      openLibraryModal,
      openArchiveModal,
      closeAddEditModal,
      closeDeleteModal,
      closeArchiveModal,
      handleCancelLibrary,
      handleCreateMenuSelect,
      editMode,
      formFlowData,
      formTemplates,
      setSpaceId,
      handleChange,
      isSaveDisabled,
      handleCreateFormFlowClick,
      userSelectionMode,
      handleUserSelectionModeChange,
      toolbarClickHandler,
      archivedToolbarClickHandler,
      handleDeleteFormFlow,
      refreshGrid,
      handleBackClick,
      handleViewSelected,
    } = useFormFlowList({
      projectId,
      formCategory,
      customQuery,
      serviceUrl: formFlowServiceUrl,
      customService,
      showOnlyGrid,
      setNextBtnState,
      setFormFlowId,
      getSelectedRows: () => getRowsOf(dataGrid),
      getArchivedSelectedRows: () => getRowsOf(archivedDataGrid),
      // `refresh` is optional on SlickGrid1's ref extras but is always attached by the grid; the
      // `?.` on `current` is the existing guard and the `!` is erased at emit.
      refreshList: () => dataGrid.current?.refresh!(),
      refreshArchivedList: () => archivedDataGrid.current?.refresh!(),
      onBack: () => navigate(getProjectSettingsPageUrl({ projectId: projectId as string, tab: "projectModules" })),
    });

    const createTemplate = () => {
      return (
        <SelectDropDown
          items={createMenuItems}
          select={(args: { item: { id?: string } }) => handleCreateMenuSelect(args.item.id)}
          cssClass="e-caret-hide border-none ps-8 bg-white p-0"
        >
          <ButtonComponent id="add" buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
            <span className="flex gap-1 items-center justify-center">
              <Text>{t("Create")}</Text>
              <IconComponent name="arrowDownSm" width="1rem" height="1rem" />
            </span>
          </ButtonComponent>
        </SelectDropDown>
      );
    };

    /** The "Create" toolbar entry renders the split button; the rest are plain entries. */
    const gridToolbarOptions = toolbarOptions.map((option, index) =>
      index === 0 ? { ...option, template: createTemplate } : option,
    );

    const selectionEvent = (args: Record<string, unknown>) => {
      if (!dataGrid?.current) return;
      handleSelectionChange((args.detail as GridSelectionDetail).args.rows.length);
    };

    const archivedSelectionEvent = (args: Record<string, unknown>) => {
      if (!archivedDataGrid.current) return;
      handleArchivedSelectionChange((args.detail as GridSelectionDetail).args.rows.length);
    };

    const recordClick = (args: GridClickEvent) => {
      const rowIndex = args.detail.args.row;
      handleRecordClick({
        rowData: dataGrid.current!.slickGrid.getDataItem(rowIndex) as FormFlowRow,
        cell: args.detail.args.cell,
      });
    };

    const formatCreatedDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: FormFlowRow,
    ) => {
      return convertDate(Number(dataContext.createdDate), "dateForTooltip");
    };

    const createdByTemplate = (
      _row: number,
      _cell: number,
      _value: FormFlowRow["createdBy"],
      _columnDef: unknown,
      dataContext: FormFlowRow,
    ) => {
      const container = document.createElement("div");
      const _id = typeof dataContext.createdBy === "object" ? dataContext.createdBy?._id : dataContext.createdBy;
      renderUserTag(container, { id: _id });
      return container;
    };

    // Column metadata comes from the shared constants; only the cell renderers are web-specific.
    const columnDefinitions = useMemo(
      () =>
        columns.map((column) => {
          switch (column.id) {
            case "selectedForm":
              return {
                ...column,
                formatter: (
                  _row: number,
                  _cell: number,
                  _value: FormFlowRow["selectedForm"],
                  _columnDef: unknown,
                  dataContext: FormFlowRow,
                ) => _.get(dataContext, "selectedForm.name"),
                _valueGetter: (row: FormFlowRow) => row.selectedForm?.name,
              };
            case "space":
              return {
                ...column,
                formatter: (
                  _row: number,
                  _cell: number,
                  _value: FormFlowRow["space"],
                  _columnDef: unknown,
                  dataContext: FormFlowRow,
                ) => _.get(dataContext, "space.spaceName"),
                _valueGetter: (row: FormFlowRow) => row.space?.spaceName,
              };
            case "createdBy":
              return {
                ...column,
                formatter: createdByTemplate,
                _valueGetter: (row: FormFlowRow) =>
                  typeof row.createdBy === "object" ? row.createdBy?.fullName : row.createdBy,
              };
            case "createdDate":
              return { ...column, formatter: formatCreatedDate };
            default:
              return column;
          }
        }),
      [columns],
    );

    const headerContent = () => (
      <div className=" w-full flex justify-between rounded-md">
        <Text className="">{editMode ? t("Edit Form Flow") : t("Create New Form Flow")}</Text>
        {!editMode && (
          <SelectSpaces
            disablePermissionCheck
            data-testid="spaceDropdownField"
            projectId={projectId}
            selectedSpaceCallback={(params: { _id: string }) => {
              setSpaceId(params._id);
            }}
          />
        )}
      </div>
    );

    const modelContent = () => (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Text>{t("Form Flow Name")}</Text>
          <span>
            <TextFieldComponent
              name="name"
              type="text"
              placeholder={t("Add form flow name")}
              showClearButton
              onChange={handleChange}
              value={formFlowData.name}
            />
          </span>
        </div>
        <div className="flex flex-col gap-2">
          <Text>{t("Select Template")}</Text>
          <span>
            <SelectDropDown
              searchable
              // SHAPE GAP, preserved verbatim: the dialog stores the template's `_id` STRING here
              // (`setFormFlowField` keeps `value._id`, and `startEditFormFlow` seeds the row's
              // `selectedForm._id`), while `SelectDropDown` declares `value` as an option OBJECT.
              // It has always been given a string; `FormFlowFormValues.selectedTemplate` is
              // `unknown`, so the mismatch was invisible. Asserted (erased) to keep the existing
              // behaviour — reconciling the two is a behaviour change, out of scope here.
              value={formFlowData.selectedTemplate as Record<string, unknown> | null}
              placeholder={t("Select Form")}
              disabled={formFlowData.formCategory == "" || editMode}
              onChange={(e) => handleChange({ target: { name: "selectedTemplate", value: e } })}
              buttonType={BUTTON_TYPES.BORDER_BUTTON}
              fields={{
                text: "name",
                value: "_id",
              }}
              options={formTemplates}
              buttonWidth="100%"
              popperWidth={300}
            />
          </span>
        </div>
        {!editMode &&
          getUserSelection(
            "Assign users to level",
            userSelectionMode,
            handleUserSelectionModeChange as (args: React.ChangeEvent<HTMLInputElement>) => void,
          )}
      </div>
    );

    const footerContent = () => (
      <div className="flex gap-4 items-center mt-5">
        <ButtonComponent
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          disabled={isSaveDisabled}
          onClick={handleCreateFormFlowClick}
        >
          <Text>{editMode ? t("Update") : t("Create")}</Text>
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={closeAddEditModal}>
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );

    const getGridComponent = ({
      gridRef,
      query,
      onSelectionChange,
      toolbarClick,
      toolbar,
      onRowClick,
      onDataLoaded,
      onClickLayout,
      emptyState,
    }: {
      gridRef: React.MutableRefObject<FormFlowGridRef | null>;
      query: Record<string, unknown>;
      onSelectionChange: (args: Record<string, unknown>) => void;
      toolbarClick: (params: string) => void;
      /** `null` in selection-only mode, where the list renders without a toolbar. */
      toolbar: ToolbarItem[] | null;
      onRowClick?: (args: GridClickEvent) => void;
      onDataLoaded: () => void;
      onClickLayout?: (layoutParams: LayoutData) => void;
      emptyState: () => React.ReactElement;
    }) => (
      <SlickGrid1
        ref={gridRef}
        onClickLayout={onClickLayout}
        // Column metadata is assembled from the shared constants and cannot go through
        // `typedColumns()`, which is the only way to obtain the branded `ValidatedColumnDefinition`.
        // Erased at emit.
        columnDefinitions={columnDefinitions as ValidatedColumnDefinition<GridRowBase>[]}
        enableSortingOption
        onSelectedRowIdsChanged={onSelectionChange}
        remoteDataSource={listService}
        infiniteScroll
        query={query}
        onClick={onRowClick}
        enableFilteringOption
        enableGroupingOption
        enableColumnChooser
        toolbarClickHandler={toolbarClick}
        // `null` here means "no toolbar"; the prop is `ToolbarItem[] | undefined`, and passing
        // `?? undefined` instead would emit different code. Erased at emit.
        toolbar={toolbar as ToolbarItem[]}
        setGridReady={() => onDataLoaded()}
        noDataIllustration={emptyState}
        enableViews={Boolean(projectId && formCategory)}
        {...(projectId && formCategory
          ? {
              viewsOptions: {
                headerName: "Views",
                name: "View",
                resourceType: formCategory,
                gridType: FORM_FLOWS_GRID_TYPE,
                selectedLayoutKey: getFormFlowsSelectedLayoutKey(formCategory),
                fetchParams: { projectId },
              },
            }
          : {})}
      />
    );

    const emptyIllustration = ({ title, actions }: { title: string; actions?: React.ReactNode }) => (
      <div className="flex flex-col items-center justify-center gap-6 h-full w-full overflow-hidden">
        <span className="flex flex-col items-center">
          <IconComponent name="NoData" className="h-52 w-52" />
          <Text className="text-2xl text-text_gray">{title}</Text>
        </span>
        {actions}
      </div>
    );

    const archiveModal = () => (
      <ModalStateDialogComponent
        allowModalPadding={false}
        width="80%"
        height="90%"
        headerTitle={<Text className="text-2xl font-semibold">{t("Archived Form Flows")}</Text>}
        isVisible={showArchiveModal}
        onCancel={closeArchiveModal}
        modelContent={
          <div className="flex w-full h-full overflow-auto">
            <div className={`${archivedIsLoadingComplete ? "" : "invisible"} h-full w-full overflow-auto`}>
              {getGridComponent({
                gridRef: archivedDataGrid,
                query: archivedListQuery,
                onSelectionChange: archivedSelectionEvent,
                toolbarClick: archivedToolbarClickHandler,
                toolbar: archivedToolbarOptions,
                onDataLoaded: handleArchivedListLoaded,
                emptyState: () => emptyIllustration({ title: t("No Archived Form Flows") }),
              })}
            </div>
          </div>
        }
        footerContent={
          <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={closeArchiveModal}>
            <Text>{t("Close")}</Text>
          </ButtonComponent>
        }
      />
    );

    const addEditModal = () => (
      <ModalStateDialogComponent
        allowModalPadding={false}
        footerContent={footerContent()}
        width="50%"
        headerTitle={headerContent()}
        onCancel={closeAddEditModal}
        isVisible={openAddEditModal}
        modelContent={modelContent()}
      />
    );

    const deletePopup = () => (
      <ModalStateDialogComponent
        allowModalPadding={false}
        footerContent={
          <div className=" flex gap-4">
            <ButtonComponent onClick={handleDeleteFormFlow} buttonType="gradientButton">
              <Text>{t("Confirm")}</Text>
            </ButtonComponent>
            <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeDeleteModal}>
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
          </div>
        }
        headerTitle={
          <div className=" w-full flex justify-between rounded-md">
            <Text className="">{t("Delete selected form flow(s)")}</Text>
          </div>
        }
        isVisible={showDeleteConfirmationModal}
        onCancel={closeDeleteModal}
        modelContent={
          <Text className="whitespace-normal">{t("Are you sure you want to delete the selected form flow(s) ?")}</Text>
        }
        width="30%"
      />
    );

    const noAccessPage = () => (
      <div className="flex w-full h-full overflow-auto flex-col justify-center items-center gap-1">
        <img src={img403} alt="noAccess" />
        <Text className="font-semibold text-2xl">{t("You do not have access to this page")}</Text>
      </div>
    );

    const header = () => {
      return (
        <div className="flex justify-between items-center ps-2 pt-4 pb-2 pe-4 w-full">
          <div className="flex gap-4 items-center">
            <IconComponent className="cursor-pointer" onClick={handleBackClick} name="BackwardArrow" />
            <div className="flex gap-1 flex-col">
              <Text className="text-2xl font-semibold">{t("Form Flows")}</Text>
              <Text>{t("List of all the Form Flows in this Project")}</Text>
            </div>
          </div>
          <div onClick={openArchiveModal} className="flex gap-1 cursor-pointer items-center">
            <IconComponent className="h-4 w-4" name="archive" />
            <Text>{t("View Archived")}</Text>
          </div>
        </div>
      );
    };

    const getFormFlowContent = () => (
      <div className="flex w-full flex-col h-full overflow-auto">
        {!showOnlyGrid && header()}
        <div className="flex w-full h-full overflow-auto">
          <div className={`${isLoadingComplete ? "" : "invisible"} h-full w-full overflow-auto`}>
            {getGridComponent({
              gridRef: dataGrid,
              query: listQuery,
              onSelectionChange: selectionEvent,
              toolbarClick: toolbarClickHandler,
              toolbar: !showOnlyGrid ? gridToolbarOptions : null,
              onRowClick: recordClick,
              onDataLoaded: handleListLoaded,
              onClickLayout: handleViewSelected,
              emptyState: () =>
                emptyIllustration({
                  title: t("No Form Flow has been added yet"),
                  actions: showOnlyGrid ? undefined : (
                    <div className="flex flex-row gap-4">
                      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={openCreateModal}>
                        <span className="flex items-center">
                          <IconComponent className="h-4" name="add" />
                          <Text>{t("Create")}</Text>
                        </span>
                      </ButtonComponent>
                      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={openLibraryModal}>
                        <span className="flex items-center">
                          <IconComponent className="h-4" name="add" />
                          <Text>{t("Create From Library")}</Text>
                        </span>
                      </ButtonComponent>
                    </div>
                  ),
                }),
            })}
          </div>
        </div>
      </div>
    );

    const getFlowsFromLibrary = () => {
      return (
        <FormFlowLibraryPopup
          handleCancelibrary={handleCancelLibrary}
          projectId={projectId}
          refreshGrid={refreshGrid}
          addFromLibrary={addFromLibrary}
          setAddFromLibrary={setAddFromLibrary}
          resourceType={formFlowData.formCategory}
        />
      );
    };

    return (
      <>
        {hasAddPermission ? getFormFlowContent() : noAccessPage()}
        {openAddEditModal && addEditModal()}
        {showDeleteConfirmationModal && deletePopup()}
        {addFromLibrary.state && getFlowsFromLibrary()}
        {showArchiveModal && archiveModal()}
      </>
    );
  },
);
FormFlows.displayName = "FormFlows";
export default FormFlows;
